import { useCallback, useEffect, useRef } from 'react';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { SearchType } from '@mews-commander/enums';
import { SearchResult } from '@mews-commander/types';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { eqStringArray, eqUniversal } from '@mews/fp';
import { ControllerProps, useControllerWithEventHandler, useSameReferenceCache, useUUID } from '@mews/framework';
import { Identifiable } from '@mews/std';
import { openModal } from '../../../modules/app/actions/common';
import {
    clearSelectedSearchResult,
    loadSearchResult,
    preselectSearchResult,
    resetSearch,
    selectSearchResult,
} from '../../../modules/search/actions';
import { createNewSearchHookFormEntity, enhanceCustomerCreateUrl } from '../../../modules/search/actions/utils';
import Search, { SearchProps } from '../../../modules/search/components/Search';
import { getSearchResultInteractiveDetail } from '../../../modules/search/getSearchResultInteractiveDetail';
import { getSelectedSearchResult } from '../../../modules/search/selectors';

export interface SearchHookSelectFieldProps
    extends Pick<ControllerProps, 'rules'>,
        Omit<SearchProps, 'scope' | 'onChange' | 'error' | 'onBlur'> {
    createNewEntity?: (result: SearchResult, onDataReceived: (id: string) => void) => void;
    clearOnSelection?: boolean;
    onOpenResult?: () => void;
    onChange?: (event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent | null, value: string | null) => void;
    onSearchResultSelected?: (result: SearchResult | null) => void;
}

export const SearchHookField = ({
    name,
    onChange,
    rules,
    onOpenResult,
    optionalSearchParams: optionalSearchParamsValue,
    clearOnSelection = false,
    newEntity,
    searchTypes: searchTypesValue,
    createNewEntity,
    onResultClick,
    onResultClear,
    autoFocus,
    onSearchResultSelected,
    ...other
}: SearchHookSelectFieldProps) => {
    const scope = `hook-search-${name}-${useUUID()}`;
    const optionalSearchParams = useSameReferenceCache(optionalSearchParamsValue, eqUniversal);
    const searchTypes = useSameReferenceCache(searchTypesValue, eqStringArray);

    const searchRef = useRef<HTMLInputElement>(null);

    const { error, handleChange, onBlur, value } = useControllerWithEventHandler({ name, onChange, rules });

    const selectedResult = useCommanderSelector((s) => getSelectedSearchResult(s, { scope }));
    const enterpriseId = useCurrentEnterpriseId();
    const dispatch = useCommanderThunkDispatch();

    const selectResult = (result: SearchResult) => dispatch(selectSearchResult({ selectedResult: result }, scope));
    const clearResult = useCallback(() => dispatch(clearSelectedSearchResult(scope)), [dispatch, scope]);
    const preselectResult = useCallback(
        async (id: string) =>
            dispatch(
                preselectSearchResult({ enterpriseId, searchTypes, optionalParams: optionalSearchParams, id }, scope)
            ),
        [enterpriseId, scope, optionalSearchParams, dispatch, searchTypes]
    );

    const openSelectedResult = (url: string) => {
        onOpenResult?.();
        return dispatch(openModal(url));
    };
    const interactiveDetail = getSearchResultInteractiveDetail({
        searchResult: selectedResult,
        handler: openSelectedResult,
    });

    const handleClearResult = () => {
        if (onResultClear) {
            onResultClear?.();
        }
        clearResult();
        handleChange(null, null);
        onSearchResultSelected?.(null);
    };
    const handleResultClick = (result: SearchResult) => {
        selectResult(result);
        onResultClick?.(result);
        onSearchResultSelected?.(result);
        if (clearOnSelection) {
            handleClearResult();
        } else {
            handleChange(null, result.id);
        }
    };

    const onSearchSuccess = useCallback(
        (data: Identifiable, searchType: SearchType) => {
            if (searchType === SearchType.Company || searchType === SearchType.Customer) {
                // avoid refetching the company from replica DB
                dispatch(loadSearchResult({ searchType, result: [data] }, scope));
            }
            handleChange(null, data.id);
        },
        [handleChange, scope, dispatch]
    );

    const handleSelectResult = (result: SearchResult) => {
        if (result.id) {
            handleResultClick(result);
        } else if (newEntity) {
            if (createNewEntity) {
                createNewEntity(result, (id) => handleChange(null, id));
            } else {
                dispatch(
                    createNewSearchHookFormEntity(
                        {
                            searchType: result.searchType,
                            url:
                                result.searchType === SearchType.Customer
                                    ? enhanceCustomerCreateUrl(result.url)
                                    : result.url,
                        },
                        (data: Identifiable) => onSearchSuccess(data, result.searchType)
                    )
                );
            }
        }
    };

    useEffect(() => {
        const normalizedValue = value ?? null;
        const normalizedResultId = selectedResult?.id ?? null;
        if (normalizedValue !== normalizedResultId) {
            if (value) {
                void preselectResult(normalizedValue);
            } else {
                clearResult();
            }
        }
    }, [preselectResult, clearResult, selectedResult?.id, value]);

    useEffect(
        () => () => {
            dispatch(resetSearch(scope));
        },
        [dispatch, scope]
    );

    useEffect(() => {
        if (autoFocus) {
            searchRef?.current?.focus();
        }
    }, [searchRef, autoFocus]);

    const searchProps: SearchProps = {
        name,
        scope,
        error,
        searchTypes,
        onResultClick: handleSelectResult,
        onResultClear: handleClearResult,
        selectedResult,
        newEntity,
        optionalSearchParams,
        onBlur,
    };

    return <Search {...searchProps} {...interactiveDetail} {...other} ref={searchRef} />;
};
